import React, { useState } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import { getOrganizerCurrency } from '../../../utils/currency';
import { images } from '../../../assets/images';
import './EventCardDetailed.css';

interface EventCardDetailedProps {
  evt: any;
  onSelectEvent: (event: any) => void;
  onPublish?: (event: any) => void;
  onUnpublish?: (event: any) => void;
  onEdit?: (event: any) => void;
  idx?: number;
}

const formatCurrency = (val: number) => {
  const currencyInfo = getOrganizerCurrency();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyInfo.code, maximumFractionDigits: 0 }).format(val);
  } catch {
    return `${currencyInfo.symbol}${val.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
};

const EventCardDetailed: React.FC<EventCardDetailedProps> = ({
  evt,
  onSelectEvent,
  onPublish,
  onUnpublish,
  onEdit,
  idx = 0
}) => {
  const [hovered, setHovered] = useState(false);

  let isRole2 = false;
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsedData = JSON.parse(authUser);
      isRole2 = String(parsedData.user?.userRole) === "2";
    }
  } catch (err) {
    console.error("Error parsing authUser", err);
  }

  const name = evt.eventName || evt.name || "Untitled Event";
  const poster = evt.bannerImgDesktop || evt.thumbImg || evt.eventImage || evt.poster;
  const category = evt.categoryName || evt.category || "General";
  const status = evt.eventStatus || evt.status || "Draft";
  const venueType = evt.eventType || evt.venueType || "Venue";
  const venue = evt.eventVenue || evt.venue || evt.eventCity || "No Venue Listed";
  const dateStr = evt.eventDate || evt.date || new Date().toISOString();

  const ticketsSold = Number(evt.soldTicket || evt.ticketsSold || 0);
  const capacity = Number(evt.totalTicket || evt.capacity || 100);
  const revenue = Number(evt.revenue || 0);
  const percentageSold = capacity > 0 ? (ticketsSold / capacity) * 100 : 0;

  const isDraft = status.toLowerCase() === "draft";
  const isActive = status.toLowerCase() === "active" || status.toLowerCase() === "live" || status.toLowerCase() === "published";
  const isPast = status.toLowerCase() === "past" || status.toLowerCase() === "completed" || status.toLowerCase() === "closed";

  const isImage = poster && (poster.startsWith("http") || poster.startsWith("data:") || poster.startsWith("/"));
  const imageSrc = isImage ? poster : images.noEvent;

  return (
    <div
      id={`event-card-${evt.eventId || evt.id}`}
      onClick={() => onSelectEvent(evt)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`ecd-card ${hovered ? "is-hovered" : ""}`}
    >
      {/* Image Box with Portrait 3:4 aspect ratio */}
      <div className="ecd-img-wrap">
        <img src={imageSrc} alt={name} />
        <div className="ecd-overlay-gradient" />

        {/* Category overlay */}
        <span className="ecd-badge-cat">
          {category}
        </span>

        {/* Status overlay */}
        <span className={`ecd-badge-status ${
          isActive
            ? 'ecd-badge-active'
            : isDraft
            ? 'ecd-badge-draft'
            : status.toLowerCase() === 'cancelled'
            ? 'ecd-badge-cancelled'
            : 'ecd-badge-past'
        }`}>
          {isPast ? 'Completed' : isActive ? 'Live' : isDraft ? 'Draft' : status}
        </span>

        {/* Venue Type overlay */}
        <span className="ecd-badge-venue">
          {venueType}
        </span>
      </div>

      {/* Content Area */}
      <div className="ecd-content">
        <div>
          <h4 className="ecd-title" title={name}>
            {name}
          </h4>
          <div className="ecd-subtitle">
            <span className="ecd-meta-date">
              <Calendar size={12} style={{ opacity: 0.7, flexShrink: 0 }} />
              <span>{new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </span>
            <span className="ecd-meta-venue" title={venue}>
              <MapPin size={12} style={{ opacity: 0.7, flexShrink: 0 }} />
              <span className="ecd-meta-venue-text">{venue}</span>
            </span>
          </div>
        </div>

        {/* Occupancy Indicator */}
        {/* <div className="ecd-occupancy-wrap">
          <div className="ecd-occupancy-head">
            <span>OCCUPANCY</span>
            <span className="fw-bold text-white">
              {ticketsSold} / {capacity} ({percentageSold.toFixed(0)}%)
            </span>
          </div>
          <div className="ecd-bar-bg">
            <div
              className="ecd-bar-fill"
              style={{
                width: `${Math.min(percentageSold, 100)}%`,
                backgroundColor: isDraft ? '#f59e0b' : percentageSold >= 90 ? '#f43f5e' : '#10b981'
              }}
            />
          </div>
        </div> */}

        {/* Action Buttons Row */}
        {isRole2 ? (
          <div className="ecd-actions-wrap">
            <div className="ecd-sales-row mb-2">
              <div>
                <span className="ecd-sales-label d-block">SALES</span>
                <span className="ecd-sales-val">
                  {ticketsSold}
                </span>
              </div>
              <div className="text-end">
                <span className="ecd-sales-label d-block">REVENUE</span>
                <span className="ecd-rev-val">
                  {formatCurrency(revenue)}
                </span>
              </div>
            </div>
          </div>
        ) : isDraft ? (
          <div className="ecd-actions-wrap">
            <div className="ecd-draft-note mb-2">
              Draft Workspace &middot; Ready for launch
            </div>
            <div className="ecd-btns-row">
              <button
                type="button"
                className="ecd-btn-publish w-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectEvent(evt);
                }}
              >
                GO TO DRAFT
              </button>
            </div>
          </div>
        ) : (
          <div className="ecd-actions-wrap">
            <div className="ecd-btns-row">
              <button
                type="button"
                className="ecd-btn-edit"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEdit) onEdit(evt);
                }}
              >
                EDIT
              </button>
              <button
                type="button"
                className="ecd-btn-publish"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectEvent(evt);
                }}
              >
                MANAGE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCardDetailed;
